import { Box, Checkbox, Container, FormControlLabel, IconButton, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import Command from '../../action/command';
import { fetchDepots } from '../../action/depotsCommands';
import { fetchLocations } from '../../action/locationsCommands';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import PageSubtitle from '../../components/PageSubtitle';
import SearchBar from '../../components/SearchBar';
import ReloadIcon from '../../components/icons/ReloadIcon';
import {
    ISSUE_REPORTS_BOOKING_PARAMS,
    makeIssueReportDetailsUrl,
    makeRentalIssueReportDetailsUrl,
    paths,
} from '../../routes';
import { normalizeBookingNumber } from './util/issueReportBookingMatch';
import {
    issueReportsFetchStatusSelector,
    makePermittedIssueReportListSelector,
} from '../../selector/issueReportsSelector';
import IssueReportCard from './IssueReportCard';
import { IssueReport } from '../../api/types';
import { makeHasPermissionSelector, tenantSelector } from '../../selector/authenticationSelector';
import Permission from '../../common/permissions';
import CustomButton from '../../components/CustomButton';
import DropdownMenu, { DropdownMenuItem } from '../../components/DropdownMenu';
import CsvDateRangeDialog from '../../components/CsvDateRangeDialog';
import { useIsMaster } from '../../common/useIsMaster';
import { enqueueErrorSnackbar, enqueueInfoSnackbar } from '../../action/snackbarCommands';
import { downloadCsv } from '../../utils/csvDownload';
import { issueReportStateLabel } from './util/issueReportStateLabel';
import { locationListSelector } from '../../selector/locationsSelector';
import { depotListSelector } from '../../selector/depotsSelector';
import {
    buildIssueReportCsv,
    filterIssueReportsByCreatedDate,
    issueReportCsvFileName,
} from './util/issueReportCsv';


export interface IssueReportCardInfo {
    titleKey: string;
    titleTranslationKey?: string;
    subTitleKey?: string;
    descriptionKey: string;
    doorKey?: string;
}

interface IssueReportsPageProps {
    cardInfo?: IssueReportCardInfo;
    addIssueReportPath?: string;
    serviceType?: string;
}

const IssueReportsPage: React.FC<IssueReportsPageProps> = ({ cardInfo, addIssueReportPath, serviceType }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const permittedIssueReports = useSelector(
        makePermittedIssueReportListSelector(),
    );
    const fetchStatus = useSelector(issueReportsFetchStatusSelector);
    // Fetched on mount below; the CSV resolves each report's address from them.
    // The unfiltered lists: the "permitted" selectors filter by EDIT rights,
    // which would make the exported address depend on who exports.
    const locations = useSelector(locationListSelector);
    const depots = useSelector(depotListSelector);
    const selectedTenant = useSelector(tenantSelector);
    const hasCreateIssueReportsPermission = useSelector(
        makeHasPermissionSelector(Permission.createIssueReports()),
    );
    // The export is an analysis tool: Owner and Manager, not Service. Its own
    // permission (#3064), granted by the DB role map for self-service tenants
    // and by the Auth0 permission set for legacy ones.
    const isMaster = useIsMaster();
    const hasExportPermission = useSelector(
        makeHasPermissionSelector(Permission.exportIssueReports()),
    );
    const canExportCsv = isMaster || hasExportPermission;
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportDateFrom, setExportDateFrom] = useState('');
    const [exportDateTo, setExportDateTo] = useState('');

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [includeArchived, setIncludeArchived] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>('all');

    // Arriving from a booking that has more than one report: narrow the list to
    // that booking so the operator can pick, rather than guessing which report
    // they meant. Both identifiers travel and the same match rule applies.
    const [searchParams, setSearchParams] = useSearchParams();
    const bookingFilter = {
        orderId: searchParams.get(ISSUE_REPORTS_BOOKING_PARAMS.orderId),
        bookingNumber: searchParams.get(ISSUE_REPORTS_BOOKING_PARAMS.bookingNumber),
    };
    const isFilteredByBooking = !!(bookingFilter.orderId || bookingFilter.bookingNumber);

    // Mirrors `damageReportBelongsToBooking` in the backend, which is where the
    // rule lives — a list filtered in the browser cannot call it, so this is the
    // one permitted copy. Keep the two in step.
    const matchesBookingFilter = (issueReport: IssueReport): boolean => {
        if (
            bookingFilter.orderId
            && issueReport.orderId
            && issueReport.orderId === bookingFilter.orderId
        ) {
            return true;
        }
        const reportNumber = normalizeBookingNumber(issueReport.bookingNumber);
        return !!reportNumber && reportNumber === (bookingFilter.bookingNumber ?? '');
    };

    const clearBookingFilter = () => {
        const next = new URLSearchParams(searchParams);
        next.delete(ISSUE_REPORTS_BOOKING_PARAMS.orderId);
        next.delete(ISSUE_REPORTS_BOOKING_PARAMS.bookingNumber);
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        dispatch(fetchLocations({}));
        dispatch(fetchDepots({}));
    }, []);

    useEffect(() => {
        dispatch(Command.IssueReport.fetchIssueReports({ includeArchived }));
    }, [includeArchived]);

    const refresh = () => {
        dispatch(Command.IssueReport.fetchIssueReports({ includeArchived }));
    };

    const statusLabel = (status: string): string => issueReportStateLabel(status, t);

    const STATUS_OPTIONS = ['all', 'open', 'in progress', 'on hold', 'done'];
    const statusItems: DropdownMenuItem[] = STATUS_OPTIONS.map((status) => ({
        id: status,
        label: statusLabel(status),
        onClick: () => setStatusFilter(status),
    }));

    const navigateToIssueReport = (issueReportId: string) => {
        const serviceMakeCallable: Record<string, any> = {
            apartment: makeRentalIssueReportDetailsUrl,
        };
        if (serviceType && Object.keys(serviceMakeCallable).includes(serviceType)) {
            navigate(serviceMakeCallable[serviceType](String(issueReportId)));
        } else {
            navigate(makeIssueReportDetailsUrl(String(issueReportId)));
        }
    };

    const navigateToAddIssueReport = () => {
        navigate(addIssueReportPath ?? paths.addIssueReports);
    };

    const reloadIssueReports = () => {
        dispatch(fetchLocations({}));
        dispatch(Command.IssueReport.fetchIssueReports({ includeArchived }));
        dispatch(fetchDepots({}));
    };

    const filteredIssueReports = permittedIssueReports
        // The store is shared with the detail pages, which load archived
        // reports too; the toggle decides what this list shows.
        .filter((issueReport) => includeArchived || !issueReport.archivedAt)
        .filter((issueReport) =>
            isFilteredByBooking
                ? matchesBookingFilter(issueReport)
                : true,
        )
        .filter((issueReport) =>
            statusFilter === 'all'
                ? true
                : (issueReport.state ?? '').toLowerCase() === statusFilter.toLowerCase(),
        )
        .filter((issueReport) => {
            if (searchQuery.length < 3) {
                return true;
            }
            return issueReport.message
                .toLowerCase()
                .includes(searchQuery.toLowerCase());
        });

    const issueReportList = filteredIssueReports.sort(
        (issueReport1, issueReport2) => {
            if (
                new Date(issueReport1.createdAt) >
                new Date(issueReport2.createdAt)
            )
                return -1;
            if (
                new Date(issueReport1.createdAt) <
                new Date(issueReport2.createdAt)
            )
                return 1;
            return 0;
        },
    );

    const closeExportDialog = () => {
        setExportDialogOpen(false);
        setExportDateFrom('');
        setExportDateTo('');
    };

    // A file built from a list that never loaded would be empty or stale and
    // look like "no reports" — so say why there is nothing to export instead.
    const openExportDialog = () => {
        if (fetchStatus === 'failed') {
            dispatch(enqueueErrorSnackbar({ message: t('issue_reports.csv.load_failed') }));
            return;
        }
        if (fetchStatus !== 'loaded') {
            dispatch(enqueueInfoSnackbar({ message: t('issue_reports.csv.still_loading') }));
            return;
        }
        if (issueReportList.length === 0) {
            dispatch(enqueueInfoSnackbar({ message: t('issue_reports.csv.nothing_in_view') }));
            return;
        }
        setExportDialogOpen(true);
    };

    const exportCsv = () => {
        const range = { dateFrom: exportDateFrom || undefined, dateTo: exportDateTo || undefined };
        const inRange = filterIssueReportsByCreatedDate(issueReportList, range);
        if (inRange.length === 0) {
            dispatch(enqueueInfoSnackbar({ message: t('issue_reports.csv.nothing_in_range') }));
            return;
        }
        downloadCsv(
            issueReportCsvFileName(selectedTenant ?? 'kolula', i18n.language, range),
            buildIssueReportCsv(inRange, t, {
                locationsById: new Map(locations.map((location) => [location.id, location])),
                depotLocationIds: new Map(depots.map((depot) => [depot.id, depot.locationId])),
            }, i18n.language),
        );
        closeExportDialog();
    };

    const getCardInfo = useCallback((issueReport: IssueReport) => {
        if (cardInfo) {
            return {
                title: `${cardInfo.titleTranslationKey 
                    ? getI18n().t(cardInfo.titleTranslationKey) 
                    : ''} ${issueReport[cardInfo.titleKey as keyof IssueReport] as string}`,
                subTitle: cardInfo.subTitleKey
                    ? issueReport[cardInfo.subTitleKey as keyof IssueReport] as string
                    : '',
                description: cardInfo.descriptionKey
                    ? issueReport[cardInfo.descriptionKey as keyof IssueReport] as string
                    : '',
            };
        } else {
            return {
                // A report doesn't have to concern a locker (payment issues,
                // app problems, …), so don't title those with a blank
                // "Locker" — name them for what they are.
                title: issueReport.door != null
                    ? `${t('issue_report.locker')} ${issueReport.door}`
                    : t('issue_report.general_issue_title'),
                subTitle: '',
                description: issueReport.message,
            };
        }
    }, []);

    return (
        <Container className={'px-0 sm:px-4'} maxWidth={false}>
            <Box sx={{ py: 3 }}>
                <PageHeader>
                    <PageHeadline>
                        {t('issue_reports.title')}
                    </PageHeadline>
                    <PageSubtitle>
                        {t('issue_reports.count', {
                            count: filteredIssueReports.length,
                        })}
                    </PageSubtitle>
                    <SearchBar
                        className={'hidden sm:block'}
                        placeholder={t('issue_reports.search_placeholder')}
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />
                    <Tooltip title={'Reload'}>
                        <IconButton
                            className={'text-foreground'}
                            onClick={reloadIssueReports}
                        >
                            <ReloadIcon />
                        </IconButton>
                    </Tooltip>
                    {canExportCsv && (
                        <CustomButton
                            title={t('issue_reports.csv.export_button')}
                            onClick={openExportDialog}
                            isFilled={false}
                            fullWidth={false}
                        />
                    )}
                    {
                        hasCreateIssueReportsPermission && (
                            <CustomButton
                                title={t('issue_reports.create_button')}
                                onClick={navigateToAddIssueReport}
                                isFilled={true}
                                fullWidth={false}
                            />                     
                        )
                    }
                </PageHeader>
                <SearchBar
                    className={'w-full sm:hidden mb-5'}
                    placeholder={t('issue_reports.search_placeholder')}
                    value={searchQuery}
                    onChange={setSearchQuery}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 1,
                        mb: 3,
                    }}
                >
                    <Box sx={{ width: { xs: '100%', sm: 220 } }}>
                        <DropdownMenu
                            label={t('issue_reports.archive.status_filter_label')}
                            value={statusLabel(statusFilter)}
                            items={statusItems}
                            fullWidth
                        />
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeArchived}
                                onChange={(_, checked) => setIncludeArchived(checked)}
                                name={'include-archived'}
                            />
                        }
                        label={t('issue_reports.archive.toggle')}
                        sx={{ ml: 0, color: '#374151' }}
                    />
                </Box>
                {/* A silently shortened list reads as "there is nothing else".
                    Say that a filter is on, which booking it is, and offer the
                    way out. */}
                {isFilteredByBooking && (
                    <div
                        className={
                            'flex flex-wrap items-center justify-between gap-2 mb-3 ' +
                            'rounded-[5px] bg-text-contrast px-4 py-3 shadow-sm'
                        }
                    >
                        <span className={'text-sm text-foreground'}>
                            {t('issue_reports.filtered_by_booking', {
                                bookingNumber:
                                    bookingFilter.bookingNumber
                                    || t('common.not_applicable'),
                            })}
                        </span>
                        <CustomButton
                            title={t('issue_reports.filtered_by_booking_clear')}
                            onClick={clearBookingFilter}
                            isFilled={false}
                            fullWidth={false}
                        />
                    </div>
                )}
                {/* Arriving from a booking that counted reports and finding an
                    empty grid must not read as "there are none". This list is
                    fed by the legacy store, which has no loading or error state
                    of its own, so the honest wording covers both. */}
                {isFilteredByBooking && issueReportList.length === 0 && (
                    <p className={'text-sm text-foreground/60 mb-3'}>
                        {t('issue_reports.filtered_by_booking_empty')}
                    </p>
                )}
                <section
                    className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-full gap-y-2 gap-x-5'}
                >
                    {issueReportList.map((issueReport) => (
                        <IssueReportCard
                            key={issueReport.id}
                            onPress={() => navigateToIssueReport(issueReport.id)}
                            issueReport={issueReport}
                            cardInfo={getCardInfo(issueReport)}
                            onChanged={refresh}
                        />
                    ))}
                </section>
            </Box>
            <CsvDateRangeDialog
                open={exportDialogOpen}
                dateFrom={exportDateFrom}
                dateTo={exportDateTo}
                onDateFromChange={setExportDateFrom}
                onDateToChange={setExportDateTo}
                allPeriodsHint={t('issue_reports.csv.all_periods')}
                onCancel={closeExportDialog}
                onExport={exportCsv}
            />
        </Container>
    );
};

export default IssueReportsPage;
